import React, { useEffect, useState, ReactNode } from "react";
import { Spin } from "antd";
import { AlertTriangle } from "lucide-react";

const waitForToken = (
  timeout: number = 5000,
  interval: number = 200
): Promise<{ product_id: string; environment_id: string } | null> => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const intervalId = setInterval(() => {
      const product_id = localStorage.getItem("productId");
      const environment_id = localStorage.getItem("environmentId");
      if (product_id && environment_id) {
        clearInterval(intervalId);
        resolve({ product_id, environment_id });
      }
      if (Date.now() - startTime >= timeout) {
        clearInterval(intervalId);
        resolve(null);
      }
    }, interval);
  });
};

interface SystemGuardProps {
  children: ReactNode;
}

const SystemGuard: React.FC<SystemGuardProps> = ({ children }) => {
  const [tokenReady, setTokenReady] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    waitForToken(5000, 200).then((data) => {
      if (!isMounted) return;
      if (data) {
        setTokenReady(true);
      } else {
        setError("Initialization failed. Please refresh the page.");
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  if (error) {
    return (
      <div className="w-screen h-screen flex flex-col items-center justify-center bg-[#ECF0FD] px-4">
        <AlertTriangle size={64} style={{ color: "#0525F5" }} className="mb-6" />
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Something went wrong</h2>
        <p className="text-gray-600 text-center max-w-sm mb-6">
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            backgroundColor: "#0525F5",
            color: "#ffffff",
          }}
          className="hover:opacity-90 px-5 py-2 rounded-full text-sm transition-all"
        >
          Refresh Page
        </button>
      </div>
    );
  }

  if (!tokenReady) {
    return (
      <div className="flex items-center justify-center w-screen h-screen bg-white">
        <Spin tip="Waiting for system initialization..." />
      </div>
    );
  }

  return <>{children}</>;
};

export default SystemGuard;
